import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { FormWrap } from '../FormContainer/FormContainer';

import { getApplicantData } from '../../util/helpers/applicantDataHelper';
import { useApplicantData } from '../../hooks/useApplicantData';

import useStepPost from '../../hooks/useStepPost';
import { selectApplication } from '../../store/selectors/applicationSelectors';
import { APPLICANT_TYPES, APPLICATION_TYPE_STRING } from '../../util/constants';
import useApplicationOwnerCountry from '../../hooks/useApplicationOwnerCountry';
import {
  extractValue,
  hashedMask,
  isPositiveInteger,
} from '../../util/helpers/ssnHelper';
import usePersonSsnDispatch from '../../hooks/usePersonSsnDispatch';
import { IconButton } from '../IconButton/IconButton';
import { ReactComponent as EyeOn } from '../../assets/images/svg/eye-on.svg';
import { ReactComponent as EyeOff } from '../../assets/images/svg/eye-off.svg';
import InputText from '../Fields/InputText';
import OnBoardingCta from '../OnBoarding/OnBoardingCta';
import themeColors from '../../assets/styles/themeColors';
import { retrieveFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import {
  ACCOUNT_TYPE_GTM,
  IS_SALES_ASSESSTED_REGISTRATION_FLOW,
} from '../../constants/sessionStorage';
import SaveAndCloseModal from '../Modals/SaveAndCloseModal/SaveAndCloseModal';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import { ACCOUNT_STEP_COMPLETE, ACCOUNT_STEP_VIEW } from '../../util/gtmEvents';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;

  button {
    position: absolute;
    top: 32px;
    right: 0;
    margin: 12px;
  }

  svg {
    width: 24px;
    height: 24px;
    color: ${themeColors.colorSvgSemidark};
  }

  input {
    padding-right: 42px;
  }
`;
const SsnForm = ({
  nextPath,
  applicantType,
  onModalClose,
  formId,
  setEventType,
  eventType,
}) => {
  const { t } = useTranslation();
  const ApplicantData = useApplicantData(applicantType);
  const Application = useSelector(selectApplication);
  const { ApplicationType } = Application;
  const { onSuccess } = useStepPost({ nextPath, onModalClose });
  const [actualSsn, setActualSsn] = useState('');
  const [maskedSsn, setMaskedSsn] = useState('');
  const { identityCountry } = useApplicationOwnerCountry(applicantType);
  const { personSsnDispatch } = usePersonSsnDispatch();
  const [isVisible, setIsVisible] = useState(false);
  const isSalesAssisted = retrieveFromSessionStorage(
    IS_SALES_ASSESSTED_REGISTRATION_FLOW,
  );
  const [saveAndCloseModalOpen, setSaveAndCloseModalOpen] = useState(false);
  const [hasError, setHasError] = useState(false);
  const isUTMA =
    applicantType === APPLICANT_TYPES.MINOR &&
    APPLICATION_TYPE_STRING[ApplicationType] === APPLICATION_TYPE_STRING[5];

  const defaultValues = {
    Ssn: getApplicantData(ApplicantData, 'PersonSsn', 'Ssn'),
  };

  const { handleSubmit, control, setError, clearErrors } = useForm({
    defaultValues,
  });

  useEffect(() => {
    const val = getApplicantData(ApplicantData, 'PersonSsn', 'Ssn');
    if (val !== null) {
      setActualSsn(extractValue(val));
      setMaskedSsn(hashedMask(extractValue(val)));
    }
  }, [ApplicantData]);

  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  useEffect(() => {
    if (
      applicantType === APPLICANT_TYPES.MAIN &&
      ApplicantData?.PersonName &&
      ApplicantData?.PersonBirthday
    ) {
      gtmStepHelper({
        gtmStepEvent,
        eventName: ACCOUNT_STEP_VIEW,
        stepLabel: 'Secure Social Security Number',
        accountType: accountGtmType,
        accountName: ApplicantData?.PersonName,
        accountPhone: ApplicantData?.Phone,
        accountDateOfBirth: ApplicantData?.PersonBirthday,
        customParams: {
          timestamp: new Date().toISOString(),
        },
      });
      return;
    }
    if (ApplicantData?.PersonName && ApplicantData?.PersonBirthday) {
      gtmStepHelper({
        gtmStepEvent,
        eventName: ACCOUNT_STEP_VIEW,
        stepLabel: 'Secure Social Security Number Second Owner',
        accountType: accountGtmType,
        accountName: ApplicantData?.PersonName,
        accountDateOfBirth: ApplicantData?.PersonBirthday,
        accountPhone: ApplicantData?.Phone,
        customParams: {
          timestamp: new Date().toISOString(),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGtmEvent = () => {
    const birthDate = ApplicantData?.PersonBirthday;
    if (applicantType === APPLICANT_TYPES.MAIN) {
      gtmStepHelper({
        gtmStepEvent,
        eventName: ACCOUNT_STEP_COMPLETE,
        stepLabel: 'Secure Social Security Number',
        accountType: accountGtmType,
        accountName: ApplicantData?.PersonName,
        accountPhone: ApplicantData?.Phone,
        accountDateOfBirth: birthDate,
        customParams: {
          timestamp: new Date().toISOString(),
        },
      });
      return;
    }
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: 'Secure Social Security Number Second Owner',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountPhone: ApplicantData?.Phone,
      accountDateOfBirth: birthDate,
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });
  };

  const onSubmit = () => {
    if (!ApplicantData?.PersonUid) {
      return;
    }
    if (isSalesAssisted && actualSsn === '') {
      personSsnDispatch({
        personUid: ApplicantData?.PersonUid,
        data: {
          Ssn: actualSsn,
        },
        defaultValues,
        onSuccess:
          eventType === 'Form'
            ? onSuccess
            : () => {
                setHasError(false);
                setSaveAndCloseModalOpen(true);
              },
        onError:
          eventType === 'Form'
            ? () => {}
            : () => {
                setHasError(true);
                setSaveAndCloseModalOpen(true);
              },
      });
      return;
    }
    const Ssn = `${actualSsn.slice(0, 3)}-${actualSsn.slice(
      3,
      5,
    )}-${actualSsn.slice(5, 9)}`;

    if (Ssn.length === 2) {
      setError('Ssn', {
        type: 'manual',
        message: t('fields.errors.validation.ssnRequired'),
      });
      return;
    }

    if (Ssn.length < 11) {
      setError('Ssn', {
        type: 'manual',
        message: t('fields.errors.validation.ssnNotValid'),
      });
      return;
    }

    if (
      !isPositiveInteger(Ssn.slice(0, 3)) ||
      !isPositiveInteger(Ssn.slice(4, 6)) ||
      !isPositiveInteger(Ssn.slice(7, 11))
    ) {
      setError('Ssn', {
        type: 'manual',
        message: t('fields.errors.validation.ssnDigits'),
      });
      return;
    }
    personSsnDispatch({
      personUid: ApplicantData?.PersonUid,
      data: {
        Ssn,
      },
      defaultValues,
      onSuccess:
        eventType === 'Form'
          ? onSuccess
          : () => {
              setHasError(false);
              setSaveAndCloseModalOpen(true);
            },
      onError:
        eventType === 'Form'
          ? () => {}
          : () => {
              setHasError(true);
              setSaveAndCloseModalOpen(true);
            },
    });
    handleGtmEvent();
  };

  const handleChange = (event) => {
    const prevValue = maskedSsn;
    const newValue = event.target.value;
    let actual = actualSsn;

    if (newValue.length > 11) {
      return;
    }

    if (newValue.length > prevValue.length) {
      const newChar = newValue.split('').pop();
      actual = `${actual}${newChar}`;
    } else {
      const charsRemovedCount = prevValue.length - newValue.length;
      actual = actual.substr(0, actual.length - charsRemovedCount);
    }

    if (
      actual.length === 0 ||
      actual.length === 9 ||
      isPositiveInteger(actual) ||
      !isPositiveInteger(actual)
    ) {
      clearErrors();
    }

    setActualSsn(actual);
    setMaskedSsn(hashedMask(actual, true, isVisible));
  };
  useEffect(() => {
    setMaskedSsn(hashedMask(actualSsn, true, isVisible));
  }, [actualSsn, isVisible]);

  return (
    <FormWrap id={formId} onSubmit={handleSubmit(onSubmit)}>
      <Controller
        control={control}
        render={({ field: { onChange, value, ...field }, fieldState }) => (
          <Container>
            <InputText
              label={t(
                isUTMA ? 'secureIdentitySSN.ssnUTMA' : 'secureIdentitySSN.ssn',
              )}
              type="text"
              {...field}
              {...fieldState}
              placeholder="XXX-XX-XXXX"
              onChange={handleChange}
              value={maskedSsn}
              autocomplete="off"
              autoFocus
            />
            <IconButton
              type="button"
              title={`${isVisible ? 'Hide SSN' : 'Show SSN'}`}
              onClick={() => setIsVisible(!isVisible)}
            >
              {isVisible ? <EyeOn /> : <EyeOff />}
            </IconButton>
          </Container>
        )}
        name="Ssn"
      />

      <OnBoardingCta
        type="submit"
        onClick={() => setEventType('Form')}
        disabled={identityCountry !== 'USA' && !isSalesAssisted}
        title={nextPath ? t('continue') : t('update')}
      />
      {saveAndCloseModalOpen && (
        <SaveAndCloseModal
          isOpen={saveAndCloseModalOpen}
          onClose={() => setSaveAndCloseModalOpen(false)}
          hasError={hasError}
          onSuccess={onSuccess}
        />
      )}
    </FormWrap>
  );
};

SsnForm.propTypes = {
  nextPath: PropTypes.string,
  applicantType: PropTypes.string,
  onModalClose: PropTypes.func,
  formId: PropTypes.string,
  eventType: PropTypes.string,
  setEventType: PropTypes.func,
};

export default SsnForm;
